import { useQueryClient } from '@tanstack/react-query';
import {
    useCallback,
    useEffect,
    useMemo,
    useReducer,
    useRef,
    useState,
} from 'react';
import { useLocalStorage } from 'usehooks-ts';
import {
    fetchDicomListTargets,
    fetchDicomQuery,
    fetchDicomQueryPatients,
    fetchDicomRetrieve,
    fetchDicomRetrievals,
    fetchPatientsGetById,
    fetchPatientsResolve,
} from '~/backend/connector-next-api/connector-next-components';
import type {
    PatientDetailDto,
    RetrievalStatusDto,
} from '~/backend/connector-next-api/connector-next-schemas';
import { startDeadline } from '~/backend/requestDeadline';
import { retrievalProgressStateOf } from '~/containers/patient-list/model';
import {
    arrivalEvidence,
    canGoBackToResults,
    canLookUp,
    canRequest,
    flattenRemoteSeries,
    includedSources,
    initialPacsImportState,
    isRequesting,
    isTypedSearchReady,
    observeArrivals,
    pacsImportReducer,
    patientHolders,
    queryableTargets,
    remotePatients,
    SEARCH_DEBOUNCE_MS,
    selectedRemoteSeries,
    settleOnSilence,
    type AcceptedSeries,
    type ObservedDataset,
    type ObservedRetrieval,
    type PacsImportState,
    type PacsTarget,
    type RetrievalOutcome,
    type SourcedPatient,
} from '../model';

/**
 * Drives {@link pacsImportReducer} against the connector: lists queryable sources, looks a patient
 * up in every included source as the user types, reads the picked patient's series from every
 * source holding them, and issues one retrieval per selected series from the source chosen for it.
 *
 * Every network result is tagged with the generation it was started in, and the reducer drops any
 * whose generation is no longer live, so a slow answer to a superseded query can never overwrite a
 * newer one. Failures are reported as messages the reducer owns; none of them carry a patient
 * identifier, because a remote's error text is not something this UI may put on screen verbatim.
 *
 * A superseded lookup is never cancelled on the wire. Cancelling the HTTP request makes the
 * connector end the DICOM association mid-query, and ending an association under a source's DICOM
 * service mid-operation can take that service down (ARIA 18's DB Daemon terminates when a C-MOVE's
 * association is aborted). So each source runs at most one lookup at a time, a newer term waits for
 * it, and a superseded answer is simply dropped when it arrives.
 */

/** Where the sources the user excluded from lookups are remembered, per browser. */
const EXCLUDED_SOURCES_KEY = 'dicomImport.excludedSources';

/**
 * How often the connector's patient record is re-read while instances arrive. Slow enough that a
 * long transfer does not hammer the API, brisk enough that the count on screen is not visibly
 * behind what the patient list beside it already shows.
 */
const ARRIVAL_POLL_MS = 1000;

/**
 * The connector's patient record reduced to what an arrival is judged on.
 *
 * Only downloaded slices count towards the numerator. A slice carries its own status, and one that
 * failed must not be counted as received on a screen whose figure a user reads as "how much of this
 * series is here".
 */
function observedDatasets(detail: PatientDetailDto): ObservedDataset[] {
    return (detail.studies ?? []).flatMap((study) =>
        (study.series ?? [])
            .filter((series) => !!series.seriesInstanceUid)
            .map((series) => ({
                seriesInstanceUid: series.seriesInstanceUid as string,
                status: series.status ?? undefined,
                datasetType: series.datasetType ?? undefined,
                expectedSliceCount: series.expectedSliceCount ?? undefined,
                downloadedCount: (series.slices ?? []).filter(
                    (slice) => slice.status === 'Downloaded'
                ).length,
            }))
    );
}

/** The outcome names this client knows; the schema types the field as a bare string. */
function retrievalOutcomeOf(
    outcome: string | null | undefined
): RetrievalOutcome | undefined {
    return outcome === 'Requested' ||
        outcome === 'Delivered' ||
        outcome === 'Failed'
        ? outcome
        : undefined;
}

/**
 * The connector's retrieval records reduced to the verdict an arrival is judged on.
 *
 * A row carrying no handle or an outcome this client does not know is dropped rather than guessed
 * at: an unreadable verdict is no verdict, and a series with none falls back to the patient record
 * and the no-contact deadline, which can never read as arrived.
 */
function observedRetrievals(
    status: readonly RetrievalStatusDto[]
): ObservedRetrieval[] {
    return status.flatMap((row) => {
        const outcome = retrievalOutcomeOf(row.outcome);
        const state = retrievalProgressStateOf(row.state);
        return row.retrievalId === undefined ||
            outcome === undefined ||
            state === undefined
            ? []
            : [
                  {
                      retrievalId: row.retrievalId,
                      outcome,
                      state,
                      failureReason: row.failureReason ?? undefined,
                      expectedInstanceCount:
                          row.expectedInstanceCount ?? undefined,
                  },
              ];
    });
}

/**
 * How many retrieval handles one status query may name. The connector refuses a longer list, and
 * a patient's series arrive fully selected, so a large selection is read in consecutive batches
 * rather than losing every verdict to one refusal.
 */
const RETRIEVAL_STATUS_BATCH = 50;

/** The connector's own record of each retrieval, in the order the handles were given. */
async function readRetrievals(
    retrievalIds: readonly string[]
): Promise<ObservedRetrieval[]> {
    const records: ObservedRetrieval[] = [];
    for (
        let from = 0;
        from < retrievalIds.length;
        from += RETRIEVAL_STATUS_BATCH
    ) {
        const batch = retrievalIds.slice(from, from + RETRIEVAL_STATUS_BATCH);
        const status = await fetchDicomRetrievals({
            queryParams: { ids: batch.join(',') },
            // Read by the arrival poll, whose next tick is the retry.
            retryPolicy: 'none',
        });
        records.push(...observedRetrievals(status));
    }
    return records;
}

export type UsePacsImport = {
    readonly state: PacsImportState;
    readonly targets: readonly PacsTarget[];
    /** True while the source list is still being fetched, so the sources bar can say so. */
    readonly isLoadingTargets: boolean;
    readonly canLookUp: boolean;
    readonly canRequest: boolean;
    readonly isRequesting: boolean;
    readonly canGoBackToResults: boolean;
    readonly actions: {
        /** Includes a source in lookups, or excludes it; remembered for this browser. */
        readonly toggleSource: (source: string) => void;
        readonly setSearch: (value: string) => void;
        /** Looks the term up now, whatever its length. */
        readonly lookUp: () => void;
        readonly selectPatient: (picked: SourcedPatient) => void;
        readonly backToResults: () => void;
        readonly toggleSeries: (key: string) => void;
        readonly toggleSeriesGroup: (
            keys: readonly string[],
            selected: boolean
        ) => void;
        readonly chooseSeriesSource: (key: string, source: string) => void;
        readonly request: () => void;
        readonly reset: () => void;
    };
};

/**
 * Failure ids, never the remote's own words: a peer's error text may name a patient, and this UI
 * may not put that on screen.
 */
const LOOKUP_FAILED = { id: 'patientList.import.pacs.error.lookupFailed' };

/**
 * Delay before the one follow-up refresh of the patient list.
 *
 * A retrieval is accepted long before it lands: the remote opens its own association to the
 * connector's listener and the instances arrive over it. So the refresh on acceptance usually shows
 * a list that has not changed yet, and this second one catches the small transfers that finish
 * while the dialog is still open. It is a nudge, not a completion signal, and nothing in the UI may
 * present it as one: a large series keeps arriving long after it fires, which is why the pane says
 * the series arrive rather than that they have arrived.
 */
const FOLLOW_UP_REFRESH_MS = 3000;

/**
 * Deadline for one retrieval request.
 *
 * It has to exceed the connector's own `Dicom:OperationTimeoutSeconds` (30 s by default), which
 * bounds the instance-count query this endpoint runs against the remote before it starts the
 * transfer: a slow peer must be reported by the server as a refusal rather than cut off here,
 * where the outcome would be unknown. What is left for this deadline is the HTTP exchange itself
 * going dead, and nothing else ends that wait. The stage the request runs in withholds every way
 * of dismissing the dialog, so an unbounded request is a wizard locked for the rest of the session.
 *
 * It bounds the whole run, not each request in it: the first expiry stops the loop, so a connector
 * that has gone quiet costs one deadline rather than one per selected series.
 */
export const RETRIEVE_REQUEST_TIMEOUT_MS = 60_000;

export function usePacsImport(open: boolean): UsePacsImport {
    const queryClient = useQueryClient();
    const generationRef = useRef(0);
    const nextGeneration = useCallback((): number => {
        generationRef.current += 1;
        return generationRef.current;
    }, []);

    const [state, dispatch] = useReducer(
        pacsImportReducer,
        initialPacsImportState(0)
    );
    const [targets, setTargets] = useState<PacsTarget[]>([]);
    const [isLoadingTargets, setIsLoadingTargets] = useState(true);

    // Latest-ref so the async callbacks below read current keys without becoming dependencies that
    // would re-create them (and restart nothing) on every keystroke.
    const stateRef = useRef(state);
    /**
     * What a live arrival is being followed for, captured when the request settles. A ref because
     * the polling effect must not restart on every observation it dispatches.
     */
    const followedRef = useRef<{
        accepted: readonly AcceptedSeries[];
        dicomPatientId: string;
    }>({ accepted: [], dicomPatientId: '' });
    useEffect(() => {
        stateRef.current = state;
    });

    // Read once per opening, never while the dialog is closed. Reachability is a live probe: the
    // connector answers this by opening a DICOM association to every configured source, so firing
    // it from a mounted-but-closed dialog would send unsolicited traffic to a clinical archive on
    // every visit to the patient list. Per opening rather than per mount because a source's
    // reachability is exactly the thing that goes stale, and for the same reason the previous
    // opening's list is cleared up front rather than rendered as fresh while the read runs.
    useEffect(() => {
        if (!open) {
            return;
        }
        const controller = new AbortController();
        setTargets([]);
        setIsLoadingTargets(true);
        fetchDicomListTargets({}, controller.signal)
            .then((result) => {
                if (!controller.signal.aborted) {
                    setTargets(queryableTargets(result));
                }
            })
            .catch(() => {
                // An unreadable list leaves the sources bar empty rather than stale; "no source
                // configured" is the thing the user must act on either way.
                if (!controller.signal.aborted) {
                    setTargets([]);
                }
            })
            .finally(() => {
                if (!controller.signal.aborted) {
                    setIsLoadingTargets(false);
                }
            });
        return () => controller.abort();
    }, [open]);

    // Remembered as the sources excluded rather than those included, so a source added to the
    // configuration later is searched until the user says otherwise. The stored value is read
    // defensively: anything but a list of names is treated as no exclusion.
    const [storedExcluded, setStoredExcluded] = useLocalStorage<string[]>(
        EXCLUDED_SOURCES_KEY,
        []
    );
    const excluded = useMemo(
        () =>
            Array.isArray(storedExcluded)
                ? storedExcluded.filter(
                      (name): name is string => typeof name === 'string'
                  )
                : [],
        [storedExcluded]
    );
    const sources = useMemo(
        () => includedSources(targets, excluded),
        [targets, excluded]
    );
    useEffect(() => {
        dispatch({ type: 'sources-changed', sources });
    }, [sources]);

    const toggleSource = useCallback(
        (source: string) =>
            setStoredExcluded((previous) => {
                const list = Array.isArray(previous) ? previous : [];
                return list.includes(source)
                    ? list.filter((name) => name !== source)
                    : [...list, source];
            }),
        [setStoredExcluded]
    );

    // At most one lookup per source is on the wire. A term typed while a source is still answering
    // the previous one waits for it and replaces any other waiting term, so a burst of keystrokes
    // costs each source at most two lookups, and none is ever cancelled mid-association.
    const sourceBusyRef = useRef(new Set<string>());
    const sourceWaitingRef = useRef(
        new Map<string, { generation: number; term: string }>()
    );
    const querySource = useCallback(
        (source: string, generation: number, term: string): void => {
            if (sourceBusyRef.current.has(source)) {
                sourceWaitingRef.current.set(source, { generation, term });
                return;
            }
            sourceBusyRef.current.add(source);
            fetchDicomQueryPatients({
                queryParams: { target: source, search: term },
            })
                .then((result) =>
                    dispatch({
                        type: 'lookup-source-settled',
                        generation,
                        source,
                        patients: remotePatients(result.patients),
                        truncated: result.truncated === true,
                    })
                )
                .catch(() =>
                    dispatch({
                        type: 'lookup-source-failed',
                        generation,
                        source,
                        message: LOOKUP_FAILED,
                    })
                )
                .finally(() => {
                    sourceBusyRef.current.delete(source);
                    const waiting = sourceWaitingRef.current.get(source);
                    if (waiting !== undefined) {
                        sourceWaitingRef.current.delete(source);
                        querySource(source, waiting.generation, waiting.term);
                    }
                });
        },
        []
    );

    /** Looks the current term up in every included source, each answering on its own. */
    const startLookup = useCallback((): void => {
        const current = stateRef.current;
        if (!canLookUp(current) || isRequesting(current)) {
            return;
        }
        const term = current.search.trim();
        const generation = nextGeneration();
        dispatch({
            type: 'lookup-started',
            generation,
            term,
            sources: current.sources,
        });
        for (const source of current.sources) {
            querySource(source, generation, term);
        }
    }, [nextGeneration, querySource]);

    // Looked up as the user types, once the term is long enough and they have paused. A term that
    // is already the one on screen for the same sources is not asked again, so returning to the
    // results or re-rendering never repeats a lookup. Clearing the field clears the results.
    const searching = state.stage.kind === 'search';
    const lookedUpTerm = state.lookup?.term;
    const lookedUpSources = Object.keys(state.lookup?.bySource ?? {})
        .sort()
        .join('\u0000');
    useEffect(() => {
        if (!open || !searching) {
            return;
        }
        const term = state.search.trim();
        if (term.length === 0) {
            dispatch({ type: 'lookup-cleared' });
            return;
        }
        const sameSources =
            lookedUpSources === [...state.sources].sort().join('\u0000');
        if (
            !isTypedSearchReady(term) ||
            (term === lookedUpTerm && sameSources)
        ) {
            return;
        }
        const timer = setTimeout(startLookup, SEARCH_DEBOUNCE_MS);
        return () => clearTimeout(timer);
    }, [
        open,
        searching,
        state.search,
        state.sources,
        lookedUpTerm,
        lookedUpSources,
        startLookup,
    ]);

    const setSearch = useCallback(
        (value: string) => dispatch({ type: 'search-changed', value }),
        []
    );
    const toggleSeries = useCallback(
        (key: string) => dispatch({ type: 'series-toggled', key }),
        []
    );
    const toggleSeriesGroup = useCallback(
        (keys: readonly string[], selected: boolean) =>
            dispatch({ type: 'series-group-toggled', keys, selected }),
        []
    );
    const backToResults = useCallback(
        () =>
            dispatch({ type: 'back-to-results', generation: nextGeneration() }),
        [nextGeneration]
    );

    // Retrieved instances arrive through the connector's own listener, so the worklist behind this
    // dialog is the only place they show up. Refreshed here rather than left to the next window
    // focus, because the pane tells the user to look there.
    const followUpRef = useRef<ReturnType<typeof setTimeout>>();
    const refreshPatientList = useCallback((): void => {
        const invalidate = () =>
            void queryClient.invalidateQueries({
                queryKey: ['api', 'patients'],
            });
        invalidate();
        clearTimeout(followUpRef.current);
        followUpRef.current = setTimeout(invalidate, FOLLOW_UP_REFRESH_MS);
    }, [queryClient]);
    useEffect(() => () => clearTimeout(followUpRef.current), []);

    /**
     * Reads the picked patient's series from the source it was picked from and from every other
     * source listing the same person, each on its own: a source that cannot list its series leaves
     * the others' list standing, and the list says which source is missing from it.
     */
    const selectPatient = useCallback(
        (picked: SourcedPatient): void => {
            const current = stateRef.current;
            if (isRequesting(current) || current.lookup === undefined) {
                return;
            }
            const holders = patientHolders(
                current.lookup.bySource,
                current.sources,
                picked
            );
            const generation = nextGeneration();
            dispatch({ type: 'patient-selected', generation, picked, holders });

            for (const source of holders) {
                fetchDicomQuery({
                    queryParams: {
                        target: source,
                        patientId: picked.patient.dicomPatientId,
                    },
                })
                    .then((result) =>
                        dispatch({
                            type: 'studies-source-settled',
                            generation,
                            source,
                            // Echoed back by the connector, and checked by the reducer against the
                            // patient this query was issued for.
                            dicomPatientId: result.patientId ?? '',
                            rows: flattenRemoteSeries(result),
                            truncated: result.truncated === true,
                        })
                    )
                    .catch(() =>
                        dispatch({
                            type: 'studies-source-failed',
                            generation,
                            source,
                        })
                    );
            }
        },
        [nextGeneration]
    );

    const chooseSeriesSource = useCallback(
        (key: string, source: string) =>
            dispatch({ type: 'series-source-chosen', key, source }),
        []
    );

    const request = useCallback((): void => {
        const current = stateRef.current;
        if (!canRequest(current) || current.stage.kind !== 'browsing') {
            return;
        }
        const { rows, selected, patient, retrieveFrom } = current.stage;
        const chosen = selectedRemoteSeries(rows, selected);
        const generation = nextGeneration();
        dispatch({ type: 'request-started', generation });

        // One request per series, sequentially. The connector deduplicates concurrent retrievals of
        // the same series, but issuing them in order keeps the counts monotonic and means a refusal
        // is attributable to one series rather than to a batch. Progress is reported once per
        // series whatever its outcome, so it counts the series asked for, not only the accepted.
        void (async () => {
            const accepted: AcceptedSeries[] = [];
            let refused = 0;
            let unanswered = 0;
            let notRequested = 0;

            for (const [index, row] of chosen.entries()) {
                const deadline = startDeadline(RETRIEVE_REQUEST_TIMEOUT_MS);
                let expired = false;
                try {
                    const started = await fetchDicomRetrieve(
                        {
                            body: {
                                // The source chosen for this series; every row has one, since
                                // only a source that listed the series can be chosen for it.
                                target: retrieveFrom.get(row.key),
                                studyInstanceUid: row.studyInstanceUid,
                                seriesInstanceUid: row.seriesInstanceUid,
                                // The subject: the connector lists the patient from this
                                // request on and records the series with it, so the transfer
                                // can be followed, cancelled and retried from the patient list
                                // once this dialog has closed.
                                dicomPatientId: patient.dicomPatientId,
                                dicomPatientName: patient.dicomPatientName,
                                modality: row.modality,
                                seriesDescription: row.seriesDescription,
                            },
                        },
                        deadline.signal
                    );
                    accepted.push({
                        key: row.key,
                        studyInstanceUid: row.studyInstanceUid,
                        seriesInstanceUid: row.seriesInstanceUid,
                        modality: row.modality,
                        seriesDescription: row.seriesDescription,
                        expectedInstances:
                            typeof started.expectedInstanceCount === 'number'
                                ? started.expectedInstanceCount
                                : undefined,
                        // The handle this request's own record is addressed by. Another client
                        // re-requesting the same series gets its own, so the verdict followed here
                        // is this request's and not that one's.
                        retrievalId: started.retrievalId,
                    });
                } catch {
                    // One series the source will not send must not abandon the rest: each retrieval
                    // is independent, and the report names how many were refused. A request the
                    // deadline cut off is counted apart from those: the connector detaches the
                    // transfer from the request before it answers, so an answer that never arrived
                    // cannot say whether one is running. The cut may have landed while the
                    // connector was still asking the source for the instance count, which leaves
                    // the transfer unstarted, or after it was detached and already under way.
                    expired = deadline.expired();
                    if (expired) {
                        unanswered += 1;
                    } else {
                        refused += 1;
                    }
                } finally {
                    deadline.clear();
                }
                // The first expiry ends the run. The deadline bounds one request, so a connector
                // that accepts the connection and stops answering would otherwise hold this stage,
                // and with it every way of dismissing the dialog, for one deadline per selected
                // series; the listing arrives fully selected, which makes that ceiling the
                // patient's whole series count. The series left unasked are tallied on their own:
                // no request was issued for them, which is what makes asking again safe.
                if (expired) {
                    notRequested = chosen.length - index - 1;
                }
                dispatch({
                    type: 'request-progress',
                    generation,
                    accepted: accepted.length,
                    refused,
                    unanswered,
                    notRequested,
                });
                if (expired) {
                    break;
                }
            }

            followedRef.current = {
                accepted,
                dicomPatientId: patient.dicomPatientId,
            };
            dispatch({
                type: 'request-settled',
                generation,
                accepted,
                refused,
                unanswered,
                notRequested,
            });
            // Only when something was accepted: a wholly refused request changed nothing to show.
            if (accepted.length > 0) {
                refreshPatientList();
            }
        })();
    }, [nextGeneration, refreshPatientList]);

    const arrivingGeneration =
        state.stage.kind === 'arriving' ? state.generation : undefined;

    /**
     * Follows the accepted series into the connector's own records while their instances arrive.
     *
     * Two records are read, and the retrieval record is read first. It carries the connector's own
     * verdict on each requested transfer, which is what says a series is whole; the patient record
     * carries the counts and the per-series errors it is reported with. Read the other way round, a
     * verdict would be paired with a patient record taken before it and a delivered series could be
     * shown as still arriving. The verdicts are read in their own attempt and the last ones stand
     * when a read fails, so a blip cannot regress a series the connector has already concluded and
     * a connector that does not serve them at all degrades to the patient record plus the deadline.
     *
     * Polled rather than pushed: the instances travel over a DICOM association the browser has no
     * view of, and the connector exposes no stream of it. The patient record is read rather than
     * the series zip endpoint because that endpoint builds and returns the whole series once it is
     * complete, so polling it would download the study in order to ask whether it had finished.
     *
     * The loop ends when the reducer leaves `arriving`, which it does only once every accepted
     * series is terminal, and when the dialog closes and retires the generation. A poll that throws
     * is swallowed and retried: the record not being readable for a moment is not evidence about
     * the transfer, and reporting it as one would turn a blip into a failed import.
     *
     * How long the whole retrieval has gone without the connector recording anything is carried
     * into every observation. It backs only a request the connector still reports in flight, which
     * is the one ending nothing else can report: a conclusion that was never written.
     */
    useEffect(() => {
        if (arrivingGeneration === undefined) {
            return;
        }
        const followed = followedRef.current;
        const retrievalIds = followed.accepted.flatMap((series) =>
            series.retrievalId === undefined ? [] : [series.retrievalId]
        );
        let cancelled = false;
        let timer: ReturnType<typeof setTimeout> | undefined;
        // Silence is measured across the whole retrieval and restarts on everything the connector
        // records: while anything is landing, the transfer is alive.
        let evidence = 0;
        let evidenceAt = Date.now();
        let retrievals: readonly ObservedRetrieval[] = [];

        const poll = async (): Promise<void> => {
            if (retrievalIds.length > 0) {
                try {
                    retrievals = await readRetrievals(retrievalIds);
                } catch {
                    // The verdicts already in hand stand. A read that failed says nothing about
                    // the transfers, and dropping a conclusion would put a settled series back
                    // under the silence deadline.
                }
            }
            try {
                // One attempt per request: the next tick is the retry.
                const resolution = await fetchPatientsResolve({
                    body: { dicomPatientId: followed.dicomPatientId },
                    retryPolicy: 'none',
                });
                // No record yet is the ordinary opening state, not a failure: the patient is
                // created by the first instance to land. The observation is dispatched either
                // way, so a retrieval that delivers nothing is judged on the connector's verdict
                // or, failing that, on its silence.
                const detail =
                    resolution.exists && resolution.patientId
                        ? await fetchPatientsGetById({
                              pathParams: { id: resolution.patientId },
                              retryPolicy: 'none',
                          })
                        : undefined;
                if (!cancelled) {
                    const observed = observeArrivals(
                        followed.accepted,
                        detail === undefined ? [] : observedDatasets(detail),
                        retrievals
                    );
                    const seen = arrivalEvidence(observed);
                    if (seen !== evidence) {
                        evidence = seen;
                        evidenceAt = Date.now();
                    }
                    dispatch({
                        type: 'arrivals-observed',
                        generation: arrivingGeneration,
                        arrivals: settleOnSilence(
                            observed,
                            Date.now() - evidenceAt
                        ),
                        patientId: resolution.patientId ?? undefined,
                    });
                    // Only a record that exists can have changed what the list shows.
                    if (detail !== undefined) {
                        refreshPatientList();
                    }
                }
            } catch {
                // Retried on the next tick.
            }
            if (!cancelled) {
                timer = setTimeout(() => void poll(), ARRIVAL_POLL_MS);
            }
        };

        void poll();
        return () => {
            cancelled = true;
            if (timer !== undefined) {
                clearTimeout(timer);
            }
        };
    }, [arrivingGeneration, refreshPatientList]);

    const reset = useCallback(
        () => dispatch({ type: 'reset', generation: nextGeneration() }),
        [nextGeneration]
    );

    // A closed dialog keeps no search term: the term is a patient identifier, and a shared
    // workstation must not offer one operator's search to the next. The peer survives the reset,
    // so reopening offers the same source and an empty field.
    useEffect(() => {
        if (!open) {
            reset();
        }
    }, [open, reset]);

    return {
        state,
        targets,
        isLoadingTargets,
        canLookUp: canLookUp(state),
        canRequest: canRequest(state),
        isRequesting: isRequesting(state),
        canGoBackToResults: canGoBackToResults(state),
        actions: {
            toggleSource,
            setSearch,
            lookUp: startLookup,
            selectPatient,
            backToResults,
            toggleSeries,
            toggleSeriesGroup,
            chooseSeriesSource,
            request,
            reset,
        },
    };
}
